export interface RoutePoint {
  geoPos: [number, number];
  totalDistance: number;
}

export interface ObstacleStyle {
  strokeColor?: string;
  strokeWidth?: number;
  dashColor?: string;
  lineDash?: number[];
  src?: string;
  text?: string;
}

export interface ObstacleFeature {
  id: string;
  geometry: {
    type: "Point" | "MultiPoint" | "LineString";
    coordinates: number[] | number[][];
  };
  properties: ObstacleStyle;
}

export type ObstacleSpanKind = "line" | "point" | "pylon";

export interface ObstacleSpan {
  id: string;
  kind: ObstacleSpanKind;
  startDistM: number;
  endDistM: number;
  midLon: number;
  midLat: number;
  style: ObstacleStyle;
}

export interface SampleObstacleOptions {
  corridorHalfWidthM: number;
  /** sample step along each obstacle linestring segment, metres */
  obstacleStepM?: number;
}

interface RouteSegment {
  startLon: number;
  startLat: number;
  endLon: number;
  endLat: number;
  startDistM: number;
  /** length of this segment in projected metres at the segment's mean latitude */
  lengthM: number;
  /** x metres per degree longitude at the segment's mean latitude */
  mPerDegLon: number;
  /** y metres per degree latitude (≈ constant) */
  mPerDegLat: number;
}

const DEFAULT_OBSTACLE_STEP_M = 20;
const M_PER_DEG_LAT = 111132.954;

function metresPerDegLonAt(latDeg: number): number {
  return 111319.49079327357 * Math.cos((latDeg * Math.PI) / 180);
}

function buildRouteSegments(legs: RoutePoint[]): RouteSegment[] {
  const segs: RouteSegment[] = [];
  for (let i = 0; i < legs.length - 1; i++) {
    const a = legs[i];
    const b = legs[i + 1];
    if (b.totalDistance <= a.totalDistance) continue;
    const midLat = (a.geoPos[1] + b.geoPos[1]) / 2;
    const mPerDegLon = metresPerDegLonAt(midLat);
    segs.push({
      startLon: a.geoPos[0],
      startLat: a.geoPos[1],
      endLon: b.geoPos[0],
      endLat: b.geoPos[1],
      startDistM: a.totalDistance,
      lengthM: b.totalDistance - a.totalDistance,
      mPerDegLon,
      mPerDegLat: M_PER_DEG_LAT,
    });
  }
  return segs;
}

interface Projection {
  /** route-distance in metres at the projected point */
  distM: number;
  /** perpendicular distance (metres) from the query point to the route */
  perpM: number;
}

/**
 * Project a lon/lat point onto the route polyline using local equirectangular
 * metres around each segment. Returns the projection with the smallest
 * perpendicular distance.
 */
function projectOntoRoute(lon: number, lat: number, segs: RouteSegment[]): Projection | null {
  let best: Projection | null = null;
  for (const seg of segs) {
    const dxA = (lon - seg.startLon) * seg.mPerDegLon;
    const dyA = (lat - seg.startLat) * seg.mPerDegLat;
    const dxSeg = (seg.endLon - seg.startLon) * seg.mPerDegLon;
    const dySeg = (seg.endLat - seg.startLat) * seg.mPerDegLat;
    const segLenSq = dxSeg * dxSeg + dySeg * dySeg;
    if (segLenSq <= 0) continue;
    let t = (dxA * dxSeg + dyA * dySeg) / segLenSq;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;
    const projX = t * dxSeg;
    const projY = t * dySeg;
    const perpDx = dxA - projX;
    const perpDy = dyA - projY;
    const perpM = Math.sqrt(perpDx * perpDx + perpDy * perpDy);
    const distM = seg.startDistM + t * seg.lengthM;
    if (best === null || perpM < best.perpM) {
      best = { distM, perpM };
    }
  }
  return best;
}

function emitPointSpans(
  feature: ObstacleFeature,
  coords: number[][],
  segs: RouteSegment[],
  corridorHalfWidthM: number,
  out: ObstacleSpan[],
  kind: ObstacleSpanKind = "point"
): void {
  for (const [lon, lat] of coords) {
    const proj = projectOntoRoute(lon, lat, segs);
    if (!proj || proj.perpM > corridorHalfWidthM) continue;
    out.push({
      id: feature.id,
      kind,
      startDistM: proj.distM,
      endDistM: proj.distM,
      midLon: lon,
      midLat: lat,
      style: feature.properties,
    });
  }
}

interface ObstacleSample {
  lon: number;
  lat: number;
}

/** Densify an obstacle linestring at ~stepM metres per sample (projected). */
function densify(coords: number[][], stepM: number): ObstacleSample[] {
  const out: ObstacleSample[] = [];
  if (coords.length === 0) return out;
  out.push({ lon: coords[0][0], lat: coords[0][1] });
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1];
    const b = coords[i];
    const midLat = (a[1] + b[1]) / 2;
    const mPerDegLon = metresPerDegLonAt(midLat);
    const dx = (b[0] - a[0]) * mPerDegLon;
    const dy = (b[1] - a[1]) * M_PER_DEG_LAT;
    const segLenM = Math.sqrt(dx * dx + dy * dy);
    const steps = Math.max(1, Math.ceil(segLenM / stepM));
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      out.push({ lon: a[0] + (b[0] - a[0]) * t, lat: a[1] + (b[1] - a[1]) * t });
    }
  }
  return out;
}

function emitLineSpans(
  feature: ObstacleFeature,
  coords: number[][],
  segs: RouteSegment[],
  corridorHalfWidthM: number,
  stepM: number,
  out: ObstacleSpan[]
): void {
  const samples = densify(coords, stepM);
  if (samples.length === 0) return;

  // Walk along the obstacle parametrization. Each maximal contiguous run of
  // in-corridor samples becomes one span; route-distance bounds are the min
  // and max distM seen in that run (covers both crossing and parallel cables).
  let runMin = Number.POSITIVE_INFINITY;
  let runMax = Number.NEGATIVE_INFINITY;
  let runLonSum = 0;
  let runLatSum = 0;
  let runCount = 0;

  const flush = () => {
    if (runCount === 0) return;
    out.push({
      id: feature.id,
      kind: "line",
      startDistM: runMin,
      endDistM: runMax,
      midLon: runLonSum / runCount,
      midLat: runLatSum / runCount,
      style: feature.properties,
    });
    runMin = Number.POSITIVE_INFINITY;
    runMax = Number.NEGATIVE_INFINITY;
    runLonSum = 0;
    runLatSum = 0;
    runCount = 0;
  };

  for (const s of samples) {
    const proj = projectOntoRoute(s.lon, s.lat, segs);
    if (!proj || proj.perpM > corridorHalfWidthM) {
      flush();
      continue;
    }
    if (proj.distM < runMin) runMin = proj.distM;
    if (proj.distM > runMax) runMax = proj.distM;
    runLonSum += s.lon;
    runLatSum += s.lat;
    runCount++;
  }
  flush();
}

/**
 * Project obstacles onto a route polyline and return per-obstacle
 * route-distance spans for the portions that fall inside the corridor.
 * Cables become one span per contiguous in-corridor run; points and
 * MultiPoint vertices become one zero-length span per in-corridor vertex.
 */
export function sampleObstaclesAlongRoute(
  legs: RoutePoint[],
  obstacles: ObstacleFeature[],
  opts: SampleObstacleOptions
): ObstacleSpan[] {
  if (!legs || legs.length < 2) return [];
  if (!obstacles || obstacles.length === 0) return [];
  const corridor = opts.corridorHalfWidthM;
  if (!Number.isFinite(corridor) || corridor <= 0) return [];
  const stepM = opts.obstacleStepM ?? DEFAULT_OBSTACLE_STEP_M;

  const segs = buildRouteSegments(legs);
  if (segs.length === 0) return [];

  const spans: ObstacleSpan[] = [];
  for (const ob of obstacles) {
    const geom = ob.geometry;
    if (!geom) continue;
    if (geom.type === "Point") {
      emitPointSpans(ob, [geom.coordinates as number[]], segs, corridor, spans);
    } else if (geom.type === "MultiPoint") {
      emitPointSpans(ob, geom.coordinates as number[][], segs, corridor, spans);
    } else if (geom.type === "LineString") {
      // Render one dot per in-corridor pylon coordinate. The "pylon" kind
      // marks them as belonging to a connected cable so consumers can style
      // them with the cable's stroke colour.
      emitPointSpans(ob, geom.coordinates as number[][], segs, corridor, spans, "pylon");
    }
  }
  return spans;
}
